import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { format, differenceInDays } from 'date-fns';
import { useData } from '../contexts/DataContext';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import SafeIcon from '../common/SafeIcon';
import TankSelector from '../components/TankSelector';
import WeatherWidget from '../components/WeatherWidget';
import TimestampDisplay from '../components/TimestampDisplay';
import SectionHeader from '../components/SectionHeader';
import LastUpdated from '../components/LastUpdated';

const { FiDroplet, FiTrendingUp, FiCalendar, FiPlus, FiAlertCircle, FiCheck } = FiIcons;
const { FaFish } = FaIcons;

const Dashboard = () => {
  const {
    data,
    getLatestWaterTest,
    getLatestGrowthData,
    getUpcomingTasks,
    calculateFCR,
    getActiveTank
  } = useData();
  
  const latestWaterTest = getLatestWaterTest();
  const latestGrowthData = getLatestGrowthData();
  const upcomingTasks = getUpcomingTasks();
  const fcr = calculateFCR();
  const activeTank = getActiveTank();

  const daysUntilHarvest = data.systemInfo.targetHarvestDate
    ? differenceInDays(new Date(data.systemInfo.targetHarvestDate), new Date())
    : null;

  const estimatedBiomass = latestGrowthData
    ? (latestGrowthData.averageWeight * latestGrowthData.totalFish) / 1000
    : 0;

  return (
    <div className="p-4 space-y-4">
      {/* Header with Time */}
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <TimestampDisplay />
      </div>

      {/* Educational Tip */}
      <div className="bg-amber-50 border-l-4 border-amber-500 p-3 rounded">
        <div className="flex">
          <div className="flex-shrink-0">
            <SafeIcon icon={FiAlertCircle} className="h-5 w-5 text-amber-500" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-amber-800">Tilapia Farming Tip</h3>
            <div className="mt-1 text-sm text-amber-700">
              Tilapia are highly sensitive to temperature changes. For optimal growth, maintain water temperature between 26-30°C. 
              Sudden drops below 20°C can stress fish and increase susceptibility to disease.
            </div>
          </div>
        </div>
      </div>

      {/* Tank Selector */}
      <TankSelector />
      
      {/* Weather Widget */}
      <WeatherWidget />

      {/* System Overview Cards */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        <SectionHeader 
          title="System Overview" 
          infoText="Key metrics for your current tank. These values update as you add growth and feeding data. Regular monitoring helps you track progress toward harvest targets."
        />
        <div className="grid grid-cols-2 gap-4">
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="bg-gray-50 rounded-xl p-4 shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Fish Count</p>
                <p className="text-2xl font-bold text-gray-900">
                  {latestGrowthData?.totalFish || 0}
                </p>
              </div>
              <div className="w-10 h-10 bg-aqua-100 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FaFish} className="w-5 h-5 text-aqua-600" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">Count decreases with mortalities and harvests</p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            className="bg-gray-50 rounded-xl p-4 shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Avg Weight</p>
                <p className="text-2xl font-bold text-gray-900">
                  {latestGrowthData?.averageWeight || 0}g
                </p>
              </div>
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiTrendingUp} className="w-5 h-5 text-green-600" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">Target: 500g for market size tilapia</p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            className="bg-gray-50 rounded-xl p-4 shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Biomass</p>
                <p className="text-2xl font-bold text-gray-900">
                  {estimatedBiomass.toFixed(1)}kg
                </p>
              </div>
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiTrendingUp} className="w-5 h-5 text-purple-600" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">Ideal stocking: 10-20kg/m³ of water</p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            className="bg-gray-50 rounded-xl p-4 shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Tank Size</p>
                <p className="text-2xl font-bold text-gray-900">
                  {activeTank.size >= 1000 
                    ? `${activeTank.size/1000}kL` 
                    : `${activeTank.size}L`}
                </p>
              </div>
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiDroplet} className="w-5 h-5 text-blue-600" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">Larger tanks provide more stable conditions</p>
          </motion.div>
        </div>
        
        {latestGrowthData?.timestamp && (
          <LastUpdated 
            timestamp={latestGrowthData.timestamp} 
            showSendUpdate={true}
            onSendUpdate={() => alert("This would send an update to team members in a real application")}
          />
        )}
      </div>

      {/* Latest Water Parameters */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <SectionHeader 
            title="Water Parameters" 
            infoText="Water quality is critical for tilapia health. Monitor these parameters regularly and maintain them within the recommended ranges. Fluctuations can cause stress, reduced growth, and increased mortality."
          />
          <Link to="/water-test" className="text-aqua-600 text-sm font-medium">
            View All
          </Link>
        </div>

        {latestWaterTest ? (
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Date</span>
              <span className="text-sm font-medium">
                {format(new Date(latestWaterTest.date), 'MMM dd, yyyy')}
              </span>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="flex justify-between items-center bg-gray-50 p-2 rounded-lg">
                <span className="text-sm text-gray-600">Temperature</span>
                <div className="flex flex-col items-end">
                  <span className="text-sm font-medium">{latestWaterTest.temperature}°C</span>
                  <span className="text-xs text-green-600">Ideal: 26-30°C</span>
                </div>
              </div>
              <div className="flex justify-between items-center bg-gray-50 p-2 rounded-lg">
                <span className="text-sm text-gray-600">pH</span>
                <div className="flex flex-col items-end">
                  <span className="text-sm font-medium">{latestWaterTest.ph}</span>
                  <span className="text-xs text-green-600">Ideal: 6.5-8.5</span>
                </div>
              </div>
              <div className="flex justify-between items-center bg-gray-50 p-2 rounded-lg">
                <span className="text-sm text-gray-600">Ammonia</span>
                <div className="flex flex-col items-end">
                  <span className="text-sm font-medium">{latestWaterTest.ammonia} ppm</span>
                  <span className="text-xs text-green-600">Ideal: &lt;0.5 ppm</span>
                </div>
              </div>
              <div className="flex justify-between items-center bg-gray-50 p-2 rounded-lg">
                <span className="text-sm text-gray-600">DO</span>
                <div className="flex flex-col items-end">
                  <span className="text-sm font-medium">{latestWaterTest.dissolvedOxygen} mg/L</span>
                  <span className="text-xs text-green-600">Ideal: &gt;5 mg/L</span>
                </div>
              </div>
            </div>
            
            <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              <p><strong>Pro Tip:</strong> Test water in the morning when dissolved oxygen is typically at its lowest. This gives you the most conservative reading to ensure fish health.</p>
            </div>
            
            {latestWaterTest.timestamp && (
              <LastUpdated 
                timestamp={latestWaterTest.timestamp} 
                showSendUpdate={true}
                onSendUpdate={() => alert("This would send an update to team members in a real application")}
              />
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <SafeIcon icon={FiDroplet} className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 mb-4">No water test data yet</p>
            <Link
              to="/water-test"
              className="inline-flex items-center space-x-2 bg-aqua-500 text-white px-4 py-2 rounded-lg hover:bg-aqua-600 transition-colors"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4" />
              <span>Add First Test</span>
            </Link>
          </div>
        )}
      </div>

      {/* Upcoming Tasks */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <SectionHeader 
            title="Upcoming Tasks" 
            infoText="Scheduled activities for your farm. Add regular maintenance, feeding changes, and harvesting plans to stay organized and ensure nothing is overlooked."
          />
          <Link to="/timeline" className="text-aqua-600 text-sm font-medium">
            View All
          </Link>
        </div>

        {upcomingTasks.length > 0 ? (
          <div className="space-y-3">
            {upcomingTasks.slice(0, 3).map((task) => (
              <div
                key={task.id}
                className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg"
              >
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{task.task}</p>
                  <p className="text-xs text-gray-500">
                    Due: {format(new Date(task.dueDate), 'MMM dd, yyyy')}
                  </p>
                </div>
                <SafeIcon icon={FiAlertCircle} className="w-4 h-4 text-orange-500" />
              </div>
            ))}
            
            <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-700">
              <p><strong>Task Tip:</strong> Schedule routine tasks like filter cleaning and water changes on a regular calendar to ensure consistent water quality.</p>
            </div>
            
            <LastUpdated 
              timestamp={upcomingTasks[0]?.timestamp} 
              showSendUpdate={false}
            />
          </div>
        ) : (
          <div className="text-center py-8">
            <SafeIcon icon={FiCheck} className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No upcoming tasks</p>
          </div>
        )}
      </div>

      {/* Performance Metrics */}
      {fcr > 0 && (
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <SectionHeader 
            title="Performance" 
            infoText="Feed Conversion Ratio (FCR) measures how efficiently your fish convert feed into body mass. Lower values indicate better feed utilization. Commercial tilapia operations typically achieve an FCR between 1.4-1.8."
          />
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Feed Conversion Ratio (FCR)</span>
            <span className="text-lg font-bold text-gray-900">{fcr.toFixed(2)}</span>
          </div>
          <div className="mt-2">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-aqua-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(100, (2 / fcr) * 100)}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Excellent (&lt;1.5)</span>
              <span>Good (1.5-2.0)</span>
              <span>Poor (&gt;2.0)</span>
            </div>
            <p className="text-sm text-gray-700 mt-2 p-2 bg-gray-50 rounded-lg">
              {fcr < 1.5 
                ? 'Excellent FCR! Your fish are efficiently converting feed into growth.' 
                : fcr < 2.0 
                  ? 'Good FCR. Consider minor adjustments to feeding strategy to optimize growth.' 
                  : 'FCR needs improvement. Review feeding amount, frequency, and water quality.'}
            </p>
            
            <div className="p-3 mt-3 bg-green-50 rounded-lg text-sm text-green-700">
              <p><strong>Feeding Tip:</strong> To improve FCR, consider using floating feed and feeding multiple small portions throughout the day rather than one large feeding. This reduces waste and improves digestion efficiency.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;